import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, FilePenLine, Save, Type, Upload } from "lucide-react";
import { toast } from "sonner";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { api, fetchFileBlob } from "@/lib/api";

const fmtSize = (n) => n == null ? "-" : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
const extOf = (name = "") => name.toLowerCase().slice(name.lastIndexOf("."));
const officeExts = new Set([".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx"]);

export default function FileEditor() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const path = params.get("path") || "";
  const fromPath = params.get("from") || path.split("/").slice(0, -1).join("/");
  const explorerBackUrl = `/explorer${fromPath ? `?path=${encodeURIComponent(fromPath)}` : ""}`;
  const onlyOfficeRef = useRef(null);
  const [file, setFile] = useState(null);
  const [content, setContent] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfText, setPdfText] = useState("Catatan baru");
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfX, setPdfX] = useState(50);
  const [pdfY, setPdfY] = useState(50);
  const [saving, setSaving] = useState(false);
  const [loadingOffice, setLoadingOffice] = useState(false);

  const replaceFile = async (blob, filename) => {
    const form = new FormData();
    form.append("file", blob, filename);
    await api.post("/explorer/file-replace", form, { params: { path: file.path } });
  };

  const loadOnlyOfficeScript = (serverUrl) => new Promise((resolve, reject) => {
    if (window.DocsAPI) return resolve();
    const script = document.createElement("script");
    script.src = `${serverUrl}/web-apps/apps/api/documents/api.js`;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });

  const openOnlyOffice = async (requestedPath) => {
    setLoadingOffice(true);
    try {
      const { data } = await api.get("/onlyoffice/config", { params: { path: requestedPath } });
      const effectivePath = data.path || requestedPath;
      if (effectivePath !== requestedPath) navigate(`/explorer/edit?path=${encodeURIComponent(effectivePath)}&from=${encodeURIComponent(fromPath)}`, { replace: true });
      await loadOnlyOfficeScript(data.server_url || process.env.REACT_APP_ONLYOFFICE_SERVER_URL || "http://localhost:8082");
      if (onlyOfficeRef.current?.destroyEditor) onlyOfficeRef.current.destroyEditor();
      onlyOfficeRef.current = new window.DocsAPI.DocEditor("onlyoffice-editor", data.config);
      setFile({ name: data.config.document.title, path: effectivePath, size: null, storage_path: null, onlyoffice: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OnlyOffice belum siap / gagal membuka dokumen.");
      navigate(explorerBackUrl);
    } finally { setLoadingOffice(false); }
  };

  const load = async (nextPath = path) => {
    const ext = extOf(nextPath);
    if (officeExts.has(ext)) return openOnlyOffice(nextPath);
    try {
      const { data } = await api.get("/explorer/file", { params: { path: nextPath } });
      setFile(data);
      setContent(data.content || "");
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
      if (extOf(data.name) === ".pdf") {
        const url = await fetchFileBlob(data.storage_path);
        setPdfUrl(url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "File tidak ditemukan.");
      navigate(explorerBackUrl);
    }
  };

  useEffect(() => {
    load();
    return () => {
      if (onlyOfficeRef.current?.destroyEditor) onlyOfficeRef.current.destroyEditor();
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveText = async () => {
    setSaving(true);
    try {
      await api.put("/explorer/file-text", { path, content });
      toast.success("File tersimpan dan sinkron.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan."); }
    finally { setSaving(false); }
  };

  const savePdf = async () => {
    if (!pdfText.trim()) return toast.error("Isi teks dulu.");
    setSaving(true);
    try {
      const res = await api.get(`/files/${file.storage_path}`, { responseType: "arraybuffer" });
      const pdf = await PDFDocument.load(res.data);
      const pages = pdf.getPages();
      const page = pages[Math.max(0, Math.min(pages.length - 1, Number(pdfPage) - 1))];
      const font = await pdf.embedFont(StandardFonts.Helvetica);
      page.drawText(pdfText, { x: Number(pdfX), y: Number(pdfY), size: 12, font, color: rgb(0.1, 0.1, 0.1) });
      const bytes = await pdf.save();
      await replaceFile(new Blob([bytes], { type: "application/pdf" }), file.name);
      toast.success("PDF tersimpan dan sinkron.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan PDF."); }
    finally { setSaving(false); }
  };

  const replaceManualUpload = async (event) => {
    const nextFile = event.target.files?.[0];
    event.target.value = "";
    if (!nextFile) return;
    setSaving(true);
    try {
      await replaceFile(nextFile, nextFile.name);
      toast.success("File berhasil diupdate dan sinkron.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal update file."); }
    finally { setSaving(false); }
  };

  const download = async () => {
    if (!file?.storage_path) return;
    const url = await fetchFileBlob(file.storage_path);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const ext = extOf(file?.name || path);
  const isOffice = officeExts.has(ext);

  if (isOffice) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0B1118] flex flex-col">
        <div className="h-14 border-b border-white/10 bg-[#101820]/95 backdrop-blur flex items-center justify-between px-4">
          <button onClick={() => navigate(explorerBackUrl)} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold"><ArrowLeft className="w-4 h-4" /> Explorer</button>
          <div className="text-sm text-white/70 truncate px-4">{file?.name || "OnlyOffice Editor"}</div>
          <div className="text-xs text-gold">Autosave aktif</div>
        </div>
        {loadingOffice && <div className="p-6 text-white/60">Membuka OnlyOffice...</div>}
        <div id="onlyoffice-editor" className="flex-1 min-h-0" />
      </div>
    );
  }

  if (!file) return <div className="p-12 c-muted">Memuat...</div>;

  return (
    <div className="min-h-screen p-4 pt-16 sm:p-6 lg:p-8 animate-fade-up">
      <button onClick={() => navigate(explorerBackUrl)} className="mb-5 inline-flex items-center gap-2 text-sm c-muted hover:text-gold"><ArrowLeft className="w-4 h-4" /> Kembali ke Explorer</button>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-600 mb-3"><FilePenLine className="w-4 h-4" /> Edit File</div>
        <h1 className="font-display text-4xl font-medium c-strong break-all">{file.name}</h1>
        <p className="text-sm c-muted mt-2 break-all">{file.path} • {fmtSize(file.size)}</p>
      </div>

      <div className="c-card border rounded-2xl card-elev overflow-hidden">
        {ext === ".pdf" ? (
          <div className="grid lg:grid-cols-[1fr_320px] h-[calc(100vh-220px)] min-h-[680px]">
            <iframe title="PDF Preview" src={pdfUrl} className="w-full h-full bg-white" />
            <div className="p-5 border-l c-border space-y-4 c-soft">
              <h2 className="font-display text-2xl c-strong flex items-center gap-2"><Upload className="w-5 h-5 text-gold" /> Update File PDF</h2>
              <p className="text-sm c-muted">Edit PDF manual di aplikasi lain, lalu upload ulang di sini. File akan mengganti PDF asli di folder arsip.</p>
              <label className="w-full bg-gold text-white rounded-lg px-4 py-2 text-sm inline-flex items-center justify-center gap-2 cursor-pointer hover:bg-gold-dark transition-colors">
                <Upload className="w-4 h-4" /> {saving ? "Mengupload..." : "Upload PDF Pengganti"}
                <input type="file" accept="application/pdf,.pdf" onChange={replaceManualUpload} disabled={saving} className="hidden" />
              </label>
              <button onClick={download} className="w-full border border-gold text-gold rounded-lg px-4 py-2 text-sm inline-flex items-center justify-center gap-2"><Download className="w-4 h-4" /> Download</button>
            </div>
          </div>
        ) : file.editable_as_text ? (
          <>
            <div className="p-4 border-b c-border flex justify-between items-center"><span className="text-sm c-muted">Editor teks</span><button disabled={saving} onClick={saveText} className="bg-gold text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2"><Save className="w-4 h-4" /> Simpan</button></div>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} className="w-full h-[calc(100vh-270px)] min-h-[560px] p-5 font-mono text-sm c-input outline-none" spellCheck={false} />
          </>
        ) : <div className="p-8 text-sm c-muted">Format ini belum bisa diedit langsung. Gunakan Office/PDF/TXT.</div>}
      </div>
    </div>
  );
}
